import { ArrowLeft } from "lucide-react";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";

interface BackButtonProps {
  /** Fallback route when there is no history to go back to. Defaults to "/". */
  fallback?: string;
  className?: string;
  label?: string;
}

/**
 * Universal back arrow. Uses router history when available, falls back to
 * a deterministic route. Mobile-first touch target (44px).
 */
export function BackButton({ fallback = "/", className = "", label }: BackButtonProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const text = label ?? t("back_button_label");

  const onClick = () => {
    void trackEvent("back_button_used", { fallback });
    try {
      const canGoBack =
        typeof window !== "undefined" && window.history.length > 1;
      if (canGoBack) {
        router.history.back();
        return;
      }
    } catch {
      /* noop */
    }
    void navigate({ to: fallback });
  };

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={text}
      className={
        "inline-flex h-11 min-w-[44px] items-center gap-1.5 rounded-xl px-3 text-xs font-bold text-foreground/80 transition hover:bg-accent/40 hover:text-foreground " +
        className
      }
    >
      <ArrowLeft className="h-4 w-4" />
      <span>{text}</span>
    </button>
  );
}